import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
// TransformWrapper/TransformComponent moved into MCDocumentVerificationModal component
import {
  Calendar,
  Users,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Search,
  Download,
  FileText,
  BarChart3,
  CalendarDays,
  PieChart as PieChartIcon,
  User,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Target,
  Activity,
  Shield,
  Building2,
  ExternalLink,
  Timer,
  Plane,
  CornerDownRight,
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { useTabPersistence } from '../hooks/usePersistence';
import { useIsMobile } from '../hooks/useIsMobile';
import MonthNavigator from '../components/UI/MonthNavigator';
import { staffAPI, configAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Modal from '../components/UI/Modal';
import { formatDate, formatDateTime, formatDateShort, getYearInSingapore, getTodayInSingaporeTimezone, formatDateForInput, formatHoursMinutes } from '../utils/dateUtils';
import { isOffInLieuName, isOffInLieuClaimName, isOffInLieuUsageName, OIL_EXPIRY_DAYS, isNSLeaveName } from '../constants/leaveTypes';
import NSDocumentVerificationModal from '../components/NSDocumentVerificationModal';
import MCDocumentVerificationModal from '../components/MCDocumentVerificationModal';
import { confidentialAPI } from '../services/api';
import PillToggle from '../components/UI/PillToggle';
import toast from '../utils/toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// NEW: Import LeaveBalanceManager to replace BalancesTab
import LeaveBalanceManager from '../components/Leave/LeaveBalanceManager';
import LeaveApplicationDetailModal from '../components/Leave/LeaveApplicationDetailModal';
import ProvisionalHospitalisationModal from '../components/Leave/ProvisionalHospitalisationModal';
import HospitalisationReconcileModal from '../components/Leave/HospitalisationReconcileModal';
import GroupedDateLeaveModal from '../components/Leave/GroupedDateLeaveModal';
import ConfirmModal from '../components/UI/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { useClickOutside } from '../hooks/useClickOutside';
import LeavePeriodDisplay from '../components/Leave/LeavePeriodDisplay';
import { StatusBadge } from '../components/UI/StatusBadge';
import { CountBadge } from '../components/UI/CountBadge';
import { FilterButton } from '../components/UI/FilterButton';
import { Pagination } from '../components/UI/Pagination';
import { LeaveTypeBadge } from '../components/UI/LeaveTypeBadge';
import FormSelect from '../components/UI/FormSelect';
import PillSelect from '../components/UI/PillSelect';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import MultiPillSelect from '../components/UI/MultiPillSelect';
import { pluralizeDays, formatLeaveDays } from '../utils/numberUtils';
import { getLeaveTypeBadgeClass, getLeaveTypeBgClass, LEAVE_LEGEND_ITEMS } from '../utils/leaveTypeBadgeColors';
import { getDepartmentBadgeColor, getDepartmentDashboardColor, getDepartmentShortName } from '../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../config/tradeConfig';
import { TradeBadge } from '../components/UI/TradeBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import { inclusiveCalendarDays, MIN_OVERSEAS_CONSECUTIVE_DAYS } from '../utils/overseasTravelDeclaration';
import {
  getAppliedLeaveTypeName,
  getDisplayLeaveTypeName,
  getDisplayTotalDays,
  getOilCreditedDays,
  hasCrossTypeConsumption,
  formatOilCreditDaysLabel,
  getOilCreditDaysClassName,
} from '../utils/leaveDisplayUtils';
import { DurationWithConsumption, getConsumptionFormulaTitle } from '../components/Leave/ConsumptionBreakdown';

const VALID_STATUS_FILTERS = [
  'all', 'pending', 'submitted', 'hospitalisation_pending_documents', 'reconciliation_required',
  'verified', 'approved', 'rejected', 'cancelled',
];

const LeaveManagement = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Tab persistence
  const validTabs = ['overview', 'applications', 'balances', 'calendar'];
  const [activeTab, setActiveTab] = useTabPersistence('leave-management', 'overview', validTabs);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lightweight pending leave count for badge (always enabled, independent of active tab)
  const { data: pendingLeaveCount = 0 } = useQuery({
    queryKey: ['leave', 'pendingCount'],
    queryFn: () => staffAPI.getPendingLeaveCount(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    select: (response) => response.data?.count ?? 0,
  });

  // Reporting Officers list for the RO filter dropdown — fetched once, cached 10 min
  const { data: reportingOfficers = [] } = useQuery({
    queryKey: ['staff', 'reportingOfficers'],
    queryFn: async () => {
      const res = await staffAPI.getStaffList({ page_size: 500, employment_status: 'active' });
      const staff = Array.isArray(res.data) ? res.data : res.data?.results || [];
      // Keep only staff who are an RO for at least one other person
      const roIds = new Set(
        staff
          .map(s => s.reporting_officer?.staff_id || s.reporting_officer_id)
          .filter(Boolean)
      );
      return staff
        .filter(s => roIds.has(s.staff_id))
        .map(s => {
          const name = `${s.first_name || ''} ${s.last_name || ''}`.trim();
          return { staff_id: s.staff_id, full_name: name ? `${name} (${s.staff_id})` : s.staff_id };
        })
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
    staleTime: 10 * 60 * 1000,
    enabled: activeTab === 'applications',
  });

  // URL parameter handling for deep-links from notifications / Dashboard
  // (e.g. /admin/leave?tab=applications&application=<id>[&verify=true]
  //  or /admin/leave?tab=applications&status=cancelled from HR digest)
  const preSelectedApplicationId = searchParams.get('application');
  const statusFromUrl = searchParams.get('status');
  const initialStatusFilter = VALID_STATUS_FILTERS.includes(statusFromUrl)
    ? statusFromUrl
    : 'all';

  // Deep-link target: the application is fetched directly so the right modal
  // opens regardless of which page of the paginated table it sits on.
  // type: 'detail' | 'mc' | 'ns'
  const [deepLinkModal, setDeepLinkModal] = useState({ type: null, application: null });
  const closeDeepLinkModal = () => setDeepLinkModal({ type: null, application: null });

  // Overview data (from dedicated API endpoint)
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewClassification, setOverviewClassification] = useState(null);

  // Leave types and policies
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);

  // Cancel confirmation state
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, applicationId: null, employeeName: '' });
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  // Reject confirmation state
  const [rejectConfirm, setRejectConfirm] = useState({ open: false, applicationId: null, employeeName: '' });
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [hospitalisationModal, setHospitalisationModal] = useState({
    open: false, prefill: null, extendApplication: null,
  });
  const [reconcileModal, setReconcileModal] = useState({ open: false, application: null });

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState([]);
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [roFilter, setRoFilter] = useState('all');

  // Keep ?status= in sync with the applications filter (digest deep-link + shareable URL).
  useEffect(() => {
    if (VALID_STATUS_FILTERS.includes(statusFromUrl) && statusFromUrl !== statusFilter) {
      setStatusFilter(statusFromUrl);
    }
  }, [statusFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusFilterChange = useCallback((nextStatus) => {
    const value = VALID_STATUS_FILTERS.includes(nextStatus) ? nextStatus : 'all';
    setStatusFilter(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') {
        next.delete('status');
      } else {
        next.set('status', value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Server-side paginated applications data for ApplicationsTab
  const [appTabData, setAppTabData] = useState({ results: [], count: 0 });
  const [appTabPage, setAppTabPage] = useState(1);
  const [appTabLoading, setAppTabLoading] = useState(false);
  const [appTabSort, setAppTabSort] = useState({ field: 'leave_applied_at', direction: 'desc' });

  // Track which datasets have been loaded to avoid redundant fetches
  const loadedRef = useRef({ config: false, holidays: false, overview: false });

  const parseResultArray = (result) => {
    if (result.status !== 'fulfilled') return [];
    const data = result.value.data;
    return Array.isArray(data) ? data : data?.results || [];
  };

  // --- Granular data loaders ---

  const loadConfig = useCallback(async () => {
    const results = await Promise.allSettled([
      configAPI.getLeaveTypes(),
      configAPI.getDepartments(),
    ]);
    if (results[0].status === 'fulfilled') {
      setLeaveTypes(parseResultArray(results[0]));
    }
    if (results[1].status === 'fulfilled') {
      setDepartments(parseResultArray(results[1]));
    }
    loadedRef.current.config = true;
  }, []);

  const loadPublicHolidays = useCallback(async (force = false) => {
    if (loadedRef.current.holidays && !force) return;
    try {
      const result = await configAPI.getPublicHolidays();
      const holidaysArray = Array.isArray(result.data) ? result.data : result.data?.results || [];
      const holidayMap = new Map();
      holidaysArray.forEach(h => {
        const primary = h.date || h.holiday_date || h.public_holiday_date;
        const observed = h.observed_date || h.observed_on;
        const start = h.start_date;
        const end = h.end_date;
        const holidayName = h.holiday_name || h.name || 'Public Holiday';
        if (start && end) {
          const s = new Date(start);
          const e = new Date(end);
          const cursor = new Date(s.getFullYear(), s.getMonth(), s.getDate());
          while (cursor <= e) {
            const y = cursor.getFullYear();
            const m = String(cursor.getMonth() + 1).padStart(2, '0');
            const d = String(cursor.getDate()).padStart(2, '0');
            holidayMap.set(`${y}-${m}-${d}`, holidayName);
            cursor.setDate(cursor.getDate() + 1);
          }
        } else {
          if (primary) holidayMap.set(primary.includes('T') ? primary.split('T')[0] : primary, holidayName);
          if (observed) holidayMap.set(observed.includes('T') ? observed.split('T')[0] : observed, holidayName);
        }
      });
      setPublicHolidays(Array.from(holidayMap.entries()).map(([date, name]) => ({ date, name })));
    } catch {
      setPublicHolidays([]);
    }
    loadedRef.current.holidays = true;
  }, []);

  // Load overview data from the dedicated API endpoint
  const loadOverviewData = useCallback(async (force = false, classification = null) => {
    if (loadedRef.current.overview && !force) return;
    setOverviewLoading(true);
    try {
      const result = await staffAPI.getLeaveOverviewStatistics(classification);
      setOverviewData(result.data);
      loadedRef.current.overview = true;
    } catch (err) {
      console.error('Failed to load overview statistics:', err);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const handleOverviewClassificationChange = useCallback((value) => {
    setOverviewClassification(value);
    loadedRef.current.overview = false;
    loadOverviewData(true, value);
  }, [loadOverviewData]);

  // --- Tab-aware data loading ---

  const loadDataForTab = useCallback(async (tab, force = false) => {
    const promises = [];
    if (!loadedRef.current.config) promises.push(loadConfig());

    switch (tab) {
      case 'applications':
        break;
      case 'overview':
        promises.push(loadOverviewData(force));
        break;
      case 'calendar':
        promises.push(loadPublicHolidays(force));
        break;
      default:
        break;
    }
    await Promise.allSettled(promises);
  }, [loadConfig, loadOverviewData, loadPublicHolidays]);

  // Backwards-compatible refresh used by action handlers (approve / reject / cancel)
  const loadInitialData = useCallback(async () => {
    loadedRef.current.overview = false;
    await loadDataForTab(activeTab, true);
  }, [activeTab, loadDataForTab]);

  // Mount: load config + data for the initial tab
  const initialDataLoadedRef = useRef(false);

  useEffect(() => {
    if (initialDataLoadedRef.current) return;
    initialDataLoadedRef.current = true;

    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        await loadDataForTab(activeTab);
      } catch (err) {
        console.error('Error loading leave management data:', err);
        setError('Failed to load leave management data. Please try refreshing the page.');
        toast.error('Failed to load leave management data');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [activeTab, loadDataForTab]);

  // When the user switches tabs, lazily fetch any missing data
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (!initialDataLoadedRef.current) return;
    if (prevTabRef.current === activeTab) return;
    prevTabRef.current = activeTab;
    loadDataForTab(activeTab);
  }, [activeTab, loadDataForTab]);

  // Deep-link handler: fetch the targeted application directly and open the
  // matching modal (MC verification, NS verification, or detail/approval).
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (!preSelectedApplicationId || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;

    const clearDeepLinkParams = () => {
      const url = new URL(window.location.href);
      ['application', 'employee', 'verify'].forEach(p => url.searchParams.delete(p));
      window.history.replaceState({}, document.title, url.pathname + url.search);
    };

    (async () => {
      try {
        const response = await staffAPI.getLeaveApplication(preSelectedApplicationId);
        const application = response.data;
        if (!application?.id) throw new Error('Application not found');

        const typeName = getAppliedLeaveTypeName(application) || '';
        const lowerTypeName = typeName.toLowerCase();
        const isSickLeaveType = (lowerTypeName.includes('outpatient') && lowerTypeName.includes('sick'))
          || lowerTypeName.includes('hospitalisation');
        const hasUploadedMc = !!(application.has_medical_certificate
          || application.has_mc_uploaded
          || application.mc_uploaded
          || (application.leave_medical_cert_path && application.leave_medical_cert_path.trim()));

        if (isSickLeaveType && (application.mc_required || hasUploadedMc)) {
          setDeepLinkModal({ type: 'mc', application });
        } else if (isNSLeaveName(typeName)) {
          setDeepLinkModal({ type: 'ns', application });
        } else {
          setDeepLinkModal({ type: 'detail', application });
        }
        setActiveTab('applications');
      } catch (err) {
        console.error('Failed to open application from deep link:', err);
        toast.error('Could not open this application — it may have been removed or you may not have access to it.');
      } finally {
        clearDeepLinkParams();
      }
    })();
  }, [preSelectedApplicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Server-side paginated fetch for ApplicationsTab ---

  const buildAppTabParams = useCallback((page) => {
    const PAGE_SIZE = 20;
    const dirPrefix = appTabSort.direction === 'desc' ? '-' : '';
    const params = {
      page,
      page_size: PAGE_SIZE,
      ordering: `${dirPrefix}${appTabSort.field}`,
      status_in: 'pending,submitted,verified,approved,auto_approved,rejected,cancelled',
    };
    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== 'all') {
      params.status = statusFilter;
      delete params.status_in;
    }
    if (departmentFilter !== 'all') params.department = departmentFilter;
    if (leaveTypeFilter.length > 0) {
      params.leave_type_name_in = leaveTypeFilter.join(',');
    }
    if (roFilter !== 'all') params.reporting_officer_id = roFilter;

    if (dateRangeFilter !== 'all') {
      const { after, before } = computeDateRangeBounds(dateRangeFilter);
      if (after) params.start_date_after = after;
      if (before) params.start_date_before = before;
    }
    return params;
  }, [searchTerm, statusFilter, departmentFilter, leaveTypeFilter, dateRangeFilter, roFilter, appTabSort]);

  const loadAppTabPage = useCallback(async (page = 1) => {
    setAppTabLoading(true);
    try {
      const params = buildAppTabParams(page);
      const result = await staffAPI.getLeaveApplications(params);
      const data = result.data;
      setAppTabData({
        results: data?.results || (Array.isArray(data) ? data : []),
        count: data?.count ?? (Array.isArray(data) ? data.length : 0),
      });
      setAppTabPage(page);
    } catch (err) {
      console.error('Failed to load paginated applications:', err);
    } finally {
      setAppTabLoading(false);
    }
  }, [buildAppTabParams]);

  // Debounce: re-fetch when filters change (only while on applications tab)
  const appTabFilterDebounce = useRef(null);
  useEffect(() => {
    if (activeTab !== 'applications') return;
    if (appTabFilterDebounce.current) clearTimeout(appTabFilterDebounce.current);
    appTabFilterDebounce.current = setTimeout(() => loadAppTabPage(1), 300);
    return () => { if (appTabFilterDebounce.current) clearTimeout(appTabFilterDebounce.current); };
  }, [activeTab, searchTerm, statusFilter, departmentFilter, leaveTypeFilter, dateRangeFilter, roFilter, loadAppTabPage]);

  // Fetch page 1 when switching TO the applications tab
  useEffect(() => {
    if (activeTab === 'applications' && appTabData.results.length === 0 && !appTabLoading) {
      loadAppTabPage(1);
    }
  }, [activeTab]);

  // Refresh the applications tab after an action (approve/reject/cancel)
  const refreshAppTab = useCallback(() => {
    loadAppTabPage(appTabPage);
  }, [loadAppTabPage, appTabPage]);

  const handleApproveApplication = async (applicationId) => {
    setApproveLoading(true);
    try {
      await staffAPI.approveLeaveApplication(applicationId);
      toast.success('Leave application approved');
      refreshAppTab();
      loadInitialData();
      queryClient.invalidateQueries({ queryKey: ['leave', 'pendingCount'] });
      return true;
    } catch (error) {
      console.error('Error approving application:', error);
      const msg = error.response?.data?.error || 'Failed to approve application';
      toast.error(msg);
      return false;
    } finally {
      setApproveLoading(false);
    }
  };

  const promptRejectApplication = (applicationId) => {
    const app = appTabData?.results?.find(a => a.id === applicationId);
    setRejectionReason('');
    setRejectConfirm({
      open: true,
      applicationId,
      employeeName: app?.employee_name || '',
    });
  };

  const confirmRejectApplication = async () => {
    if (!rejectConfirm.applicationId) return;
    const reason = rejectionReason.trim();
    if (!reason) {
      toast.error('Please provide a rejection reason.');
      return;
    }
    setRejectLoading(true);
    try {
      await staffAPI.rejectLeaveApplication(rejectConfirm.applicationId, { reason });
      toast.success('Leave application rejected');
      setRejectConfirm({ open: false, applicationId: null, employeeName: '' });
      setRejectionReason('');
      refreshAppTab();
      loadInitialData();
      queryClient.invalidateQueries({ queryKey: ['leave', 'pendingCount'] });
    } catch (error) {
      console.error('Error rejecting application:', error);
      const msg = error.response?.data?.error || 'Failed to reject application';
      toast.error(msg);
    } finally {
      setRejectLoading(false);
    }
  };

  const promptCancelApplication = (applicationId) => {
    const app = appTabData.results.find(a => a.id === applicationId);
    setCancelConfirm({
      open: true,
      applicationId,
      employeeName: app?.employee_name || '',
    });
  };

  const confirmCancelApplication = async () => {
    if (!cancelConfirm.applicationId) return;
    setCancelLoading(true);
    try {
      await staffAPI.cancelLeaveApplication(cancelConfirm.applicationId, { cancellation_reason: cancellationReason });
      toast.success('Leave application cancelled. Balance restored.');
      setCancelConfirm({ open: false, applicationId: null, employeeName: '' });
      setCancellationReason('');
      refreshAppTab();
      loadInitialData();
      queryClient.invalidateQueries({ queryKey: ['leave', 'pendingCount'] });
      queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
    } catch (error) {
      console.error('Error cancelling application:', error);
      const msg = error.response?.data?.error || 'Failed to cancel leave application';
      toast.error(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  if (isLoading && !error) {
    return (
      <div>
        {/* Header Skeleton */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-200 rounded animate-pulse mr-3"></div>
                <div className="h-9 bg-gray-200 rounded w-64 animate-pulse"></div>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse mr-2"></div>
                <div className="h-5 bg-gray-200 rounded w-96 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="mb-8">
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
            ))}
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Content Area Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border">
              <div className="p-6 border-b border-gray-100">
                <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
              </div>
              <div className="p-6 space-y-4">
                {[1, 2, 3, 4].map(j => (
                  <div key={j} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center text-red-600">
        <AlertTriangle className="w-12 h-12 mb-4" />
        <p>{error}</p>
        <button
          onClick={loadInitialData}
          className="btn btn-admin-primary mt-4"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Leave Management</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12 flex items-center">
          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400 flex-shrink-0" />
          <span className="truncate">Comprehensive leave administration and analytics for your organization</span>
        </p>
      </div>

      {/* Tab Navigation — Overview scope toggle is a sibling outside the scroll
          strip so mobile users don't swipe past every tab to reach All/Staff/Workers. */}
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth flex-1 min-w-0">
          <button
            onClick={() => setActiveTab('applications')}
            className={`tab-btn-admin ${activeTab === 'applications' ? 'active' : ''}`}
          >
            <FileText className="w-4 h-4" />
            Applications
            {pendingLeaveCount > 0 && (
              <CountBadge count={pendingLeaveCount} theme="admin" active={activeTab === 'applications'} className="ml-2" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('overview')}
            className={`tab-btn-admin ${activeTab === 'overview' ? 'active' : ''}`}
          >
            <BarChart3 className="w-4 h-4" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('balances')}
            className={`tab-btn-admin ${activeTab === 'balances' ? 'active' : ''}`}
          >
            <PieChartIcon className="w-4 h-4" />
            Balances
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`tab-btn-admin ${activeTab === 'calendar' ? 'active' : ''}`}
          >
            <CalendarDays className="w-4 h-4" />
            Calendar
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="w-full md:w-auto md:flex-shrink-0 flex items-center gap-2">
            <PillToggle
              value={overviewClassification ?? 'all'}
              onChange={(val) => handleOverviewClassificationChange(val === 'all' ? null : val)}
              options={[
                { id: 'all', label: 'All', disabled: overviewLoading },
                { id: 'staff', label: 'Staff', disabled: overviewLoading },
                { id: 'worker', label: 'Workers', disabled: overviewLoading },
              ]}
              variant="text"
              theme="admin"
              className="w-full md:w-fit"
              fullWidth
              ariaLabel="Overview classification"
            />
            {overviewLoading && overviewData && (
              <LoadingSpinner size="sm" />
            )}
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          data={overviewData}
          isLoading={overviewLoading}
          departments={departments}
          leaveTypes={leaveTypes}
          onTabChange={setActiveTab}
          onRefresh={() => { loadedRef.current.overview = false; loadOverviewData(true, overviewClassification); }}
        />
      )}

      {activeTab === 'applications' && (
        <ApplicationsTab
          applications={appTabData.results}
          totalCount={appTabData.count}
          serverPage={appTabPage}
          onPageChange={loadAppTabPage}
          isServerLoading={appTabLoading}
          sortConfig={appTabSort}
          onSortChange={setAppTabSort}
          departments={departments}
          leaveTypes={leaveTypes}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={handleStatusFilterChange}
          departmentFilter={departmentFilter}
          setDepartmentFilter={setDepartmentFilter}
          leaveTypeFilter={leaveTypeFilter}
          setLeaveTypeFilter={setLeaveTypeFilter}
          dateRangeFilter={dateRangeFilter}
          setDateRangeFilter={setDateRangeFilter}
          roFilter={roFilter}
          setRoFilter={setRoFilter}
          reportingOfficers={reportingOfficers}
          onApprove={handleApproveApplication}
          isApproving={approveLoading}
          onReject={promptRejectApplication}
          onCancel={promptCancelApplication}
          onRefresh={() => { refreshAppTab(); loadInitialData(); }}
          onExtendHospitalisation={(app) => setHospitalisationModal({
            open: true, prefill: null, extendApplication: app,
          })}
          onReconcileHospitalisation={(app) => setReconcileModal({ open: true, application: app })}
        />
      )}

      {activeTab === 'balances' && (
        <LeaveBalanceManager
          onRefresh={loadInitialData}
        />
      )}



      {activeTab === 'calendar' && (
          <CalendarTab
            departments={departments}
            publicHolidays={publicHolidays}
            leaveTypes={leaveTypes}
          />
        )}

      <ConfirmModal
        isOpen={cancelConfirm.open}
        title="Cancel Leave Application"
        confirmLabel="Cancel Leave"
        cancelLabel="Keep"
        variant="danger"
        onConfirm={confirmCancelApplication}
        onCancel={() => { setCancelConfirm({ open: false, applicationId: null, employeeName: '' }); setCancellationReason(''); }}
        isLoading={cancelLoading}
      >
        <p className="text-sm text-gray-700">
          Are you sure you want to cancel this leave application
          {cancelConfirm.employeeName && <> for <strong>{cancelConfirm.employeeName}</strong></>}?
          If the leave was already approved, the employee's balance will be restored.
        </p>
        <div className="mt-3">
          <label htmlFor="cancellation-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for cancellation
          </label>
          <textarea
            id="cancellation-reason"
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus:border-red-500"
            placeholder="Enter reason for cancellation..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={rejectConfirm.open}
        title="Reject Leave Application"
        confirmLabel="Reject"
        cancelLabel="Back"
        variant="danger"
        onConfirm={confirmRejectApplication}
        onCancel={() => { setRejectConfirm({ open: false, applicationId: null, employeeName: '' }); setRejectionReason(''); }}
        isLoading={rejectLoading}
        confirmDisabled={!rejectionReason.trim()}
      >
        <p className="text-sm text-gray-700">
          Are you sure you want to reject this leave application
          {rejectConfirm.employeeName && <> for <strong>{rejectConfirm.employeeName}</strong></>}?
        </p>
        <div className="mt-3">
          <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for rejection <span className="text-red-600" aria-hidden="true">*</span>
            <span className="sr-only"> (required)</span>
          </label>
          <textarea
            id="rejection-reason"
            rows={3}
            className="textarea-admin"
            placeholder="Explain why the application is being rejected..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            aria-required="true"
            aria-describedby="rejection-reason-help"
          />
          <p id="rejection-reason-help" className="mt-1.5 text-xs text-gray-500">
            This reason will be shared with the applicant.
          </p>
        </div>
      </ConfirmModal>

      {/* Deep-link modals — opened directly from a notification / Dashboard
          redirect, independent of the paginated applications table. */}
      {deepLinkModal.type === 'detail' && deepLinkModal.application && (
        <LeaveApplicationDetailModal
          isOpen
          onClose={closeDeepLinkModal}
          applicationId={deepLinkModal.application.id}
          onApprove={handleApproveApplication}
          isApproving={approveLoading}
          onReject={() => { promptRejectApplication(deepLinkModal.application.id); closeDeepLinkModal(); }}
          onCancel={(id) => { promptCancelApplication(id); closeDeepLinkModal(); }}
          onRefresh={() => { refreshAppTab(); loadInitialData(); }}
          extraFooterActions={
            deepLinkModal.application.hospitalisation_ui_status ? (
              <>
                {deepLinkModal.application.hospitalisation_ui_status === 'hospitalisation_pending_documents' && (
                  <button
                    type="button"
                    className="btn btn-admin-outline"
                    onClick={() => {
                      const app = deepLinkModal.application;
                      closeDeepLinkModal();
                      setHospitalisationModal({ open: true, prefill: null, extendApplication: app });
                    }}
                  >
                    Extend coverage
                  </button>
                )}
                {deepLinkModal.application.hospitalisation_ui_status === 'reconciliation_required' && (
                  <button
                    type="button"
                    className="btn btn-admin-primary"
                    onClick={() => {
                      const app = deepLinkModal.application;
                      closeDeepLinkModal();
                      setReconcileModal({ open: true, application: app });
                    }}
                  >
                    Reconcile dates
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-admin-primary"
                  onClick={() => setDeepLinkModal({ type: 'mc', application: deepLinkModal.application })}
                >
                  Upload / verify MC
                </button>
                <button type="button" onClick={closeDeepLinkModal} className="btn btn-secondary">
                  Close
                </button>
              </>
            ) : undefined
          }
        />
      )}
      {deepLinkModal.type === 'mc' && deepLinkModal.application && (
        <MCDocumentVerificationModal
          isOpen
          onClose={closeDeepLinkModal}
          applicationId={deepLinkModal.application.id}
          application={deepLinkModal.application}
          onVerificationComplete={() => {
            closeDeepLinkModal();
            refreshAppTab();
            loadInitialData();
            queryClient.invalidateQueries({ queryKey: ['leave', 'pendingCount'] });
          }}
        />
      )}
      {deepLinkModal.type === 'ns' && deepLinkModal.application && (
        <NSDocumentVerificationModal
          isOpen
          onClose={closeDeepLinkModal}
          applicationId={deepLinkModal.application.id}
          employeeName={deepLinkModal.application.employee_name || null}
          employeeStaffCode={deepLinkModal.application.staff_code || null}
          onVerificationComplete={() => {
            closeDeepLinkModal();
            refreshAppTab();
            loadInitialData();
            queryClient.invalidateQueries({ queryKey: ['leave', 'pendingCount'] });
          }}
        />
      )}
      <ProvisionalHospitalisationModal
        isOpen={hospitalisationModal.open}
        prefill={hospitalisationModal.prefill}
        extendApplication={hospitalisationModal.extendApplication}
        onClose={() => setHospitalisationModal({ open: false, prefill: null, extendApplication: null })}
        onSuccess={() => {
          refreshAppTab();
          loadInitialData();
          queryClient.invalidateQueries({ queryKey: ['leave'] });
          queryClient.invalidateQueries({ queryKey: ['attendance'] });
        }}
      />
      <HospitalisationReconcileModal
        isOpen={reconcileModal.open}
        application={reconcileModal.application}
        onClose={() => setReconcileModal({ open: false, application: null })}
        onSuccess={() => {
          refreshAppTab();
          loadInitialData();
          queryClient.invalidateQueries({ queryKey: ['leave'] });
          queryClient.invalidateQueries({ queryKey: ['attendance'] });
        }}
      />
    </div>
  );
};

const formatApprovalDuration = (hours) => (
  formatHoursMinutes(hours, { withDays: true })
);

const describeLeaveBooked = (bookedPct, takenPct, yearElapsedPct) => {
  const yearThrough = `${Math.round(yearElapsedPct || 0)}%`;
  const takenLabel = takenPct == null || Number.isNaN(takenPct)
    ? null
    : `${takenPct}% already taken`;
  return {
    short: takenLabel
      ? `${takenLabel} · year is ${yearThrough} through`
      : `Year is ${yearThrough} through`,
    full: `${bookedPct}% of this year's Annual Leave is already approved, including leave not taken yet.${
      takenLabel ? ` ${takenLabel}.` : ''
    } The year is ${yearThrough} through.`,
  };
};

const sharePct = (count, total) => (
  total > 0 ? Math.round((count / total) * 100) : 0
);

const describeDepartmentPace = (delta) => {
  if (delta == null || Number.isNaN(delta)) {
    return { word: '', tone: 'text-gray-500' };
  }
  if (delta > 10) return { word: 'ahead', tone: 'text-green-700' };
  if (delta >= -10) return { word: 'on track', tone: 'text-green-700' };
  if (delta >= -20) return { word: 'behind', tone: 'text-amber-700' };
  return { word: 'well behind', tone: 'text-red-700' };
};

const PENDING_ACTOR_LABELS = {
  reporting_officer: 'RO',
  trade_leader: 'Trade Leader',
  hr: 'HR',
};

const TAILWIND_BG_TO_SOLID = {
  'bg-blue-100': '#3b82f6',
  'bg-orange-100': '#f97316',
  'bg-sky-100': '#0ea5e9',
  'bg-amber-100': '#f59e0b',
  'bg-red-100': '#ef4444',
  'bg-pink-100': '#ec4899',
  'bg-purple-100': '#a855f7',
  'bg-fuchsia-100': '#d946ef',
  'bg-emerald-100': '#10b981',
  'bg-green-100': '#22c55e',
  'bg-rose-100': '#f43f5e',
  'bg-gray-100': '#9ca3af',
  'bg-slate-100': '#94a3b8',
  'bg-teal-100': '#14b8a6',
  'bg-cyan-100': '#06b6d4',
  'bg-indigo-100': '#6366f1',
  'bg-violet-100': '#8b5cf6',
  'bg-lime-100': '#84cc16',
  'bg-stone-100': '#a8a29e',
};

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun',
  '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
};

// Overview Tab — rebuilt around actionable HR decisions (see leave overview audit).
const OverviewTab = ({ data, isLoading, onTabChange, onRefresh }) => {
  const [volumeMode, setVolumeMode] = useState('ytd');

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-gray-500">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-gray-400" />
        <p className="text-sm">Failed to load overview data.</p>
        <button onClick={onRefresh} className="btn btn-admin-outline mt-4">Retry</button>
      </div>
    );
  }

  const { operational, utilisation, approval_sla: approvalSla, risk, trends, attention } = data;
  const stalePending = attention?.stale_pending || [];
  const stalePendingTotal = attention?.stale_pending_total ?? stalePending.length;
  const monthlyYTD = trends?.monthly_ytd || [];
  const monthly12m = trends?.monthly_12m || [];
  const leaveTypeBreakdown = trends?.leave_type_breakdown || [];
  const workArrangementDays = trends?.work_arrangement_days || 0;
  const claimDays = trends?.claim_days || 0;
  const deptUtil = utilisation?.by_department || [];
  const projection = risk?.year_end_projection || {};
  const watchlist = projection.watchlist || [];
  const unconfigured = risk?.unconfigured_classifications || [];

  const activeEmployees = operational?.active_employees || 0;
  const onLeaveToday = operational?.on_leave_today || 0;
  const pendingByActor = operational?.pending_by_actor || {};
  const needsAction = operational?.needs_action || 0;
  const waitingOnHr = pendingByActor.hr || 0;
  const waitingOnManagers = (pendingByActor.reporting_officer || 0)
    + (pendingByActor.trade_leader || 0);
  const availablePct = activeEmployees > 0
    ? Math.round(((activeEmployees - onLeaveToday) / activeEmployees) * 1000) / 10
    : null;

  const yearElapsedPct = utilisation?.year_elapsed_pct ?? 0;
  const bookedPct = utilisation?.overall_pct ?? 0;
  const takenPct = utilisation?.taken_pct;
  const leaveBooked = describeLeaveBooked(bookedPct, takenPct, yearElapsedPct);
  const typicalApproval = formatApprovalDuration(approvalSla?.median_hours);
  const mostApprovalsWithin = formatApprovalDuration(approvalSla?.p90_hours);
  const approvalsOverThreeDays = approvalSla?.breach_72h_pct;
  const approvalSubline = approvalSla?.sample_size
    ? `Most within ${mostApprovalsWithin} · ${approvalsOverThreeDays}% take over 3 days`
    : 'No manual approvals this year';
  const approvalFullLabel = approvalSla?.sample_size
    ? `Typical time from application to approval is ${typicalApproval}. Most requests are approved within ${mostApprovalsWithin}. ${approvalsOverThreeDays}% take more than 3 days.`
    : 'No manual approvals this year.';

  const activeTrend = volumeMode === 'ytd' ? monthlyYTD : monthly12m;
  const formatMonthLabel = (m) => {
    const parts = (m || '').split('-');
    const label = MONTH_LABELS[parts[1]] || parts[1];
    return volumeMode === '12m' ? `${label} '${(parts[0] || '').slice(2)}` : label;
  };

  const chartData = activeTrend.map((m) => ({
    name: formatMonthLabel(m.month),
    Days: m.absence_days,
    isPartial: Boolean(m.is_partial),
  }));

  const mixData = leaveTypeBreakdown.slice(0, 8).map((lt) => ({
    name: lt.leave_type_name,
    value: lt.total_days,
    count: lt.count,
  }));
  const mixTotalDays = mixData.reduce((sum, entry) => sum + (entry.value || 0), 0);

  const getLeaveTypeColor = (leaveTypeName) => {
    const bgClass = getLeaveTypeBgClass(leaveTypeName);
    return TAILWIND_BG_TO_SOLID[bgClass] || '#9ca3af';
  };

  const paceBarColor = (deltaPts) => {
    if (deltaPts >= -10) return 'bg-green-500';
    if (deltaPts >= -20) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const pendingSubline = needsAction
    ? `${waitingOnHr} waiting on HR · ${waitingOnManagers} waiting on managers`
    : 'No open approvals';

  const riskDistribution = projection.risk_distribution || {};
  const withinCapCount = riskDistribution.safe ?? projection.within_cap_count ?? 0;
  const pendingDependentCount = (
    riskDistribution.pending_dependent ?? projection.pending_dependent_count ?? 0
  );
  const confirmedAtRiskCount = (
    riskDistribution.at_risk
      ?? projection.confirmed_at_risk_count
      ?? projection.over_cap_projected
      ?? 0
  );
  const riskDistributionTotal = (
    withinCapCount + pendingDependentCount + confirmedAtRiskCount
  );
  const watchlistTotal = (
    projection.watchlist_total
    ?? (confirmedAtRiskCount + pendingDependentCount)
  );
  const daysAtRiskRaw = Number(
    projection.projected_forfeiture_days
      ?? projection.projected_excess_days
      ?? 0
  );
  const daysAtRisk = Math.round(daysAtRiskRaw);
  const avgDaysEach = confirmedAtRiskCount > 0
    ? Math.round((daysAtRiskRaw / confirmedAtRiskCount) * 10) / 10
    : 0;
  const riskBarData = [{
    name: 'Risk',
    safe: withinCapCount,
    pending_dependent: pendingDependentCount,
    at_risk: confirmedAtRiskCount,
  }];
  const yearEndMetrics = [
    {
      label: 'Employees at risk',
      value: confirmedAtRiskCount,
      subline: `${sharePct(confirmedAtRiskCount, riskDistributionTotal)}% of eligible staff`,
      Icon: AlertTriangle,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
    },
    {
      label: 'Days at risk',
      value: daysAtRisk,
      subline: confirmedAtRiskCount > 0
        ? `About ${formatLeaveDays(avgDaysEach)} days each`
        : 'No confirmed excess above carry-forward caps',
      Icon: CalendarDays,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ];

  const CARD_HEIGHT = 'h-[420px]';

  return (
    <div className="space-y-6">
      {/* Row 1 — Operational now */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <button
          type="button"
          onClick={() => onTabChange('applications')}
          className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 w-full text-left hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${needsAction > 10 ? 'border-2 border-red-400' : ''}`}
          title="Open Applications tab"
        >
          <div className="flex items-start">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{needsAction}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Open Approvals</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate" title={pendingSubline}>
                {pendingSubline}
              </p>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => onTabChange('calendar')}
          className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 w-full text-left hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          title="Open Calendar tab"
        >
          <div className="flex items-start">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{onLeaveToday}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Out Today</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate">
                {onLeaveToday} of {activeEmployees}
                {availablePct != null ? ` · ${availablePct}% available` : ''}
              </p>
            </div>
          </div>
        </button>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title={approvalFullLabel}>
          <div className="flex items-start">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Timer className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {typicalApproval}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Typical Approval Time</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate" title={approvalSubline}>
                {approvalSubline}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title={leaveBooked.full}>
          <div className="flex items-start">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Activity className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{bookedPct}%</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Annual Leave Booked</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate" title={leaveBooked.full}>
                {leaveBooked.short}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2 — Year-End Annual Leave Planning */}
      <section className="space-y-4 sm:space-y-6" aria-labelledby="year-end-al-heading">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-red-600" aria-hidden="true" />
            </div>
            <h2 id="year-end-al-heading" className="text-lg font-semibold text-gray-900">
              Year-End Annual Leave Planning
            </h2>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Projected to 31 Dec · approved future leave is included · pending leave is shown separately
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          {yearEndMetrics.map((metric) => {
            const MetricIcon = metric.Icon;
            return (
              <div
                key={metric.label}
                className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6"
              >
                <div className="flex items-start">
                  <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${metric.iconBg}`}>
                    <MetricIcon
                      className={`w-4 h-4 sm:w-6 sm:h-6 ${metric.iconColor}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-2 sm:ml-4 min-w-0">
                    <p className="text-lg sm:text-2xl font-bold text-gray-900">
                      {metric.value}
                    </p>
                    <p
                      className="text-xs sm:text-sm text-gray-600 truncate"
                      title={metric.label}
                    >
                      {metric.label}
                    </p>
                    <p
                      className="text-[11px] sm:text-xs text-gray-500 mt-1 line-clamp-2"
                      title={metric.subline}
                    >
                      {metric.subline}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border overflow-clip">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              Risk Distribution
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {riskDistributionTotal} eligible employees by projected year-end position
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <p className="sr-only">
              Year-end risk: {confirmedAtRiskCount} at risk, {pendingDependentCount}
              {' '}safe if pending leave is approved, {withinCapCount} within cap.
            </p>
            {riskDistributionTotal > 0 ? (
              <ResponsiveContainer width="100%" height={36}>
                <BarChart
                  layout="vertical"
                  data={riskBarData}
                  stackOffset="expand"
                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                >
                  <XAxis type="number" hide domain={[0, 1]} />
                  <YAxis type="category" dataKey="name" hide />
                  <Bar dataKey="safe" stackId="risk" fill="#16a34a" isAnimationActive={false} />
                  <Bar dataKey="pending_dependent" stackId="risk" fill="#d97706" isAnimationActive={false} />
                  <Bar dataKey="at_risk" stackId="risk" fill="#dc2626" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-9 rounded-full bg-gray-100" />
            )}
            <ul className="flex flex-wrap gap-x-4 gap-y-2 mt-3 text-xs text-gray-600">
              <li className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-green-600" aria-hidden="true" />
                Within cap
                <CountBadge count={withinCapCount} theme="success" max={9999} />
                <span className="text-gray-400">{sharePct(withinCapCount, riskDistributionTotal)}%</span>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-amber-600" aria-hidden="true" />
                Safe if leave is approved
                <CountBadge count={pendingDependentCount} theme="orange" max={9999} />
                <span className="text-gray-400">{sharePct(pendingDependentCount, riskDistributionTotal)}%</span>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-red-600" aria-hidden="true" />
                At risk
                <CountBadge count={confirmedAtRiskCount} theme="admin" max={9999} />
                <span className="text-gray-400">{sharePct(confirmedAtRiskCount, riskDistributionTotal)}%</span>
              </li>
            </ul>

            {(projection.campaign_stages || []).length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">
                    Reminder Campaign
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Scheduled Sep–Dec employee communications and delivery totals
                  </p>
                </div>
                <ol className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
                  {projection.campaign_stages.map((stage) => (
                    <li
                      key={stage.key}
                      className="border border-gray-200 rounded-xl p-3 min-w-0"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {stage.label}
                      </p>
                      <div className="mt-1.5">
                        <StatusBadge status={stage.status} />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-2">
                        {stage.notified || 0} of {stage.audience_count || 0} notified
                      </p>
                      {(stage.failed > 0 || stage.suppressed > 0) && (
                        <p className="text-[11px] text-gray-400 mt-1">
                          {[
                            stage.failed > 0 ? `${stage.failed} failed` : null,
                            stage.suppressed > 0 ? `${stage.suppressed} skipped` : null,
                          ].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {unconfigured.length > 0 && (
              <p className="text-xs text-amber-700 mt-4">
                Carry-forward cap not configured for: {unconfigured.join(', ')}.
                These classifications are excluded from the risk count.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Row 3 — Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Absence Days */}
        <div className={`bg-white rounded-2xl border border-gray-200 ${CARD_HEIGHT} flex flex-col`}>
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900 flex items-center min-w-0">
                  <BarChart3 className="w-4 h-4 mr-2 text-red-600 flex-shrink-0" />
                  <span className="truncate">Monthly Absence Days</span>
                </h3>
                <Link
                  to="/admin/reports?tab=monthly-leave-usage"
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 flex-shrink-0"
                >
                  Usage report
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
              <PillToggle
                value={volumeMode}
                onChange={setVolumeMode}
                options={[
                  { id: 'ytd', label: 'This year' },
                  { id: '12m', label: 'Last 12 months' },
                ]}
                variant="text"
                theme="admin"
                layout="sectionHeader"
                ariaLabel="Absence days period"
              />
            </div>
          </div>
          <div className="flex-1 p-4 min-h-0">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                    formatter={(value, _name, props) => [
                      `${value} days${props?.payload?.isPartial ? ' (month in progress)' : ''}`,
                      'Absence',
                    ]}
                  />
                  <Bar dataKey="Days" radius={[3, 3, 0, 0]}>
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={`cell-${idx}`}
                        fill={entry.isPartial ? '#f59e0b' : '#dc2626'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No absence trend data available
              </div>
            )}
          </div>
        </div>

        {/* Absence Mix */}
        <div className={`bg-white rounded-2xl border border-gray-200 ${CARD_HEIGHT} flex flex-col`}>
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              <BarChart3 className="w-4 h-4 mr-2 text-red-600" />
              Absence Mix (Days this year)
            </h3>
          </div>
          <div className="flex-1 p-4 min-h-0 overflow-y-auto">
            {mixData.length > 0 ? (
              <div className="space-y-3">
                {mixData.map((entry) => {
                  const mixPct = sharePct(entry.value, mixTotalDays);
                  const typeColor = getLeaveTypeColor(entry.name);
                  return (
                    <div key={entry.name}>
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-xs text-gray-700 truncate flex-1 min-w-0 flex items-center gap-1.5">
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: typeColor }}
                            aria-hidden="true"
                          />
                          <span className="truncate">{entry.name}</span>
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatLeaveDays(entry.value)}d · {mixPct}%
                        </span>
                      </div>
                      <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full"
                          style={{ width: `${Math.min(mixPct, 100)}%`, backgroundColor: typeColor }}
                        />
                      </div>
                    </div>
                  );
                })}
                {(workArrangementDays > 0 || claimDays > 0) && (
                  <div className="border-t border-gray-100 pt-2 mt-1 space-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      Not absences
                    </p>
                    {workArrangementDays > 0 && (
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>Work arrangements (WFH / EA / On Course)</span>
                        <span className="font-medium">{workArrangementDays}d</span>
                      </div>
                    )}
                    {claimDays > 0 && (
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>Claims (Claim: Off in Lieu)</span>
                        <span className="font-medium">{claimDays}d</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No absence data available
              </div>
            )}
          </div>
        </div>

        {/* AL Pace by Department */}
        <div className={`bg-white rounded-2xl border border-gray-200 ${CARD_HEIGHT} flex flex-col`}>
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              <Target className="w-4 h-4 mr-2 text-red-600" />
              AL Booked by Department
            </h3>
          </div>
          <div className="flex-1 p-4 min-h-0 overflow-y-auto">
            {deptUtil.length > 0 ? (
              <div className="space-y-2.5">
                {[...deptUtil]
                  .sort((a, b) => (a.pace_delta_pts ?? 0) - (b.pace_delta_pts ?? 0))
                  .map((dept) => {
                    const pct = dept.utilisation_pct || 0;
                    const delta = dept.pace_delta_pts ?? (pct - yearElapsedPct);
                    const pace = describeDepartmentPace(delta);
                    const deptName = dept.department_name || 'Unassigned';
                    const deptColor = getDepartmentDashboardColor(dept.department_name);
                    return (
                      <div key={deptName}>
                        <div className="flex items-center justify-between mb-0.5 gap-2">
                          <span className="text-xs text-gray-700 truncate flex-1 min-w-0 flex items-center gap-1.5">
                            <span
                              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${deptColor.bg}`}
                              aria-hidden="true"
                            />
                            <span className="truncate">{deptName}</span>
                          </span>
                          <span className="text-xs font-medium text-gray-900 flex-shrink-0">
                            {pct}%
                            {pace.word && (
                              <span className={`font-normal ml-1 ${pace.tone}`}>
                                · {pace.word}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="relative h-2">
                          <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-2 rounded-full ${paceBarColor(delta)} transition-all`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <div
                            className="absolute -top-0.5 w-0.5 h-3 bg-slate-700 rounded-full"
                            style={{ left: `${Math.min(Math.max(yearElapsedPct, 0), 100)}%` }}
                            title={`${Math.round(yearElapsedPct)}% of the year so far`}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    );
                  })}
                <p className="text-[10px] text-gray-400 pt-1">
                  Bar is Annual Leave already booked. Marker is the year so far
                  ({Math.round(yearElapsedPct)}% through).
                  Green = on track or ahead. Amber = behind. Red = well behind.
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No department data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 4 — Attention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stale Pending */}
        <div className="bg-white rounded-2xl border border-gray-200 h-[360px] flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
                Stale Pending Applications
                <CountBadge count={stalePendingTotal} theme="warning" size="md" max={9999} className="ml-2" />
              </h3>
              {stalePending.length > 0 && (
                <button
                  type="button"
                  onClick={() => onTabChange('applications')}
                  className="text-red-600 hover:text-red-800 text-xs font-medium flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded-full px-1"
                >
                  View All
                  <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {stalePending.length > 0 ? (
              <div className="space-y-2.5">
                {stalePending.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 p-3 bg-orange-50 rounded-xl">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.employee_name}</p>
                      <p className="text-xs text-gray-500">
                        {item.leave_type_name} &middot; {formatLeaveDays(item.leave_total_days)}{' '}
                        {pluralizeDays(item.leave_total_days)}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5 truncate" title={item.pending_actor_label}>
                        {item.pending_actor_label
                          || PENDING_ACTOR_LABELS[item.pending_actor]
                          || 'Pending'}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        {item.days_pending}d ago
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm px-4 text-center">
                <CheckCircle className="w-6 h-6 mb-2 text-green-400" />
                All caught up. No stale applications.
              </div>
            )}
          </div>
        </div>

        {/* Year-End AL Watchlist */}
        <div className="bg-white rounded-2xl border border-gray-200 h-[360px] flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-600" />
                Year-End AL Watchlist
                <CountBadge count={watchlistTotal} theme="admin" size="md" max={9999} />
              </h3>
              <button
                type="button"
                onClick={() => onTabChange('balances')}
                className="text-red-600 hover:text-red-800 text-xs font-medium flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded-full px-1"
              >
                Balances
                <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {watchlist.length > 0 ? (
              <div className="space-y-2.5">
                {watchlist.map((emp) => (
                  <div
                    key={emp.employee_staff_id || emp.employee_name}
                    className="border border-gray-200 rounded-xl p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{emp.employee_name}</p>
                        <p className="text-xs text-gray-500">{emp.department_name || 'No department'}</p>
                      </div>
                      <StatusBadge status={emp.risk_band || 'at_risk'} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                      <span>
                        Forfeiture <span className="font-semibold text-gray-900">{formatLeaveDays(emp.projected_excess)}d</span>
                      </span>
                      <span>
                        Cap <span className="font-semibold text-gray-900">{emp.cf_cap}</span>
                      </span>
                      {(emp.pending_al || 0) > 0 && (
                        <span>
                          Pending <span className="font-semibold text-gray-900">{formatLeaveDays(emp.pending_al)}d</span>
                        </span>
                      )}
                    </div>
                    {(emp.latest_reminder_label || emp.latest_delivery_status) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {emp.latest_reminder_label && (
                          <span className="text-[11px] text-gray-500">
                            Last reminder: {emp.latest_reminder_label}
                          </span>
                        )}
                        {emp.latest_delivery_status && (
                          <StatusBadge status={emp.latest_delivery_status} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm px-4 text-center">
                <CheckCircle className="w-6 h-6 mb-2 text-green-400" />
                No employees projected to exceed carry-forward cap.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const APPLICATION_PAGE_SIZE = 20;
/** Complex 9-col applications table uses lg (1024) dual-view breakpoint. */
const APPLICATION_TABLE_BREAKPOINT = 1024;

// True when the date-range filter holds a specific month ('month:YYYY-MM').
const isSpecificMonthFilter = (value) =>
  typeof value === 'string' && value.startsWith('month:');

/**
 * Resolve a date-range filter value to leave_start_date bounds.
 *
 * Accepts the relative presets ('today' | 'this_week' | 'this_month' |
 * 'next_month') or a specific month encoded as 'month:YYYY-MM'. Bounds are
 * computed against leave_start_date to match the existing month-preset
 * behaviour, so the list and CSV export stay consistent.
 *
 * @param {string} value
 * @returns {{ after: string|null, before: string|null }} YYYY-MM-DD bounds
 */
const computeDateRangeBounds = (value) => {
  if (isSpecificMonthFilter(value)) {
    const [year, month] = value.slice(6).split('-').map(Number);
    if (year && month) {
      return {
        after: formatDateForInput(new Date(year, month - 1, 1)),
        before: formatDateForInput(new Date(year, month, 0)),
      };
    }
    return { after: null, before: null };
  }

  const today = new Date();
  switch (value) {
    case 'today':
      return { after: getTodayInSingaporeTimezone(), before: getTodayInSingaporeTimezone() };
    case 'this_week': {
      const ws = new Date(today); ws.setDate(today.getDate() - today.getDay());
      const we = new Date(ws); we.setDate(ws.getDate() + 6);
      return { after: formatDateForInput(ws), before: formatDateForInput(we) };
    }
    case 'this_month':
      return {
        after: formatDateForInput(new Date(today.getFullYear(), today.getMonth(), 1)),
        before: formatDateForInput(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'next_month':
      return {
        after: formatDateForInput(new Date(today.getFullYear(), today.getMonth() + 1, 1)),
        before: formatDateForInput(new Date(today.getFullYear(), today.getMonth() + 2, 0)),
      };
    default:
      return { after: null, before: null };
  }
};

// Relative date presets offered alongside the specific-month picker.
const DATE_RANGE_PRESETS = [
  { value: 'all', label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'next_month', label: 'Next Month' },
];

const DATE_RANGE_LABELS = DATE_RANGE_PRESETS.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }),
  {},
);

// Human-readable label for the active date-range chip (e.g. 'March 2026').
const formatDateRangeLabel = (value) => {
  if (isSpecificMonthFilter(value)) {
    const [year, month] = value.slice(6).split('-').map(Number);
    if (year && month) {
      return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }
  }
  return DATE_RANGE_LABELS[value] || value;
};

/**
 * Date filter as a single pill-anchored popover: relative presets + a
 * specific-month picker live together, so the filter row keeps one control per
 * dimension (no dangling inputs). Emits the same `dateRangeFilter` string that
 * computeDateRangeBounds() understands ('all' | preset | 'month:YYYY-MM').
 */
const DateRangeFilterPill = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useClickOutside(() => setIsOpen(false), isOpen);

  const isMonth = isSpecificMonthFilter(value);
  const hasSelection = value && value !== 'all';
  const monthValue = isMonth ? value.slice(6) : '';

  const selectPreset = (v) => {
    onChange(v);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className="flex items-center gap-1.5 w-full bg-white border border-gray-200 h-11 px-4 rounded-full shadow-sm hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm font-medium text-gray-900"
      >
        <Calendar className="w-4 h-4 text-red-600 flex-shrink-0" />
        <span className={`truncate flex-1 text-left ${hasSelection ? '' : 'text-gray-400'}`}>
          {hasSelection ? formatDateRangeLabel(value) : 'All Dates'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-1 w-full min-w-[240px] bg-white border border-gray-200 rounded-xl shadow-lg p-1.5">
          {DATE_RANGE_PRESETS.map((preset) => {
            const active = value === preset.value;
            return (
              <button
                key={preset.value}
                type="button"
                onClick={() => selectPreset(preset.value)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  active ? 'text-red-600 font-medium bg-red-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {preset.label}
              </button>
            );
          })}

          <div className="my-1.5 border-t border-gray-100" />

          <div className="px-3 pt-1 pb-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Specific month
            </label>
            <input
              type="month"
              aria-label="Select specific month"
              value={monthValue}
              onChange={(e) => onChange(e.target.value ? `month:${e.target.value}` : 'all')}
              className={`w-full h-10 px-3 bg-white border rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                isMonth ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200'
              }`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Applications Tab Component
const ApplicationsTab = ({
  applications,
  totalCount,
  serverPage,
  onPageChange,
  isServerLoading,
  sortConfig,
  onSortChange,
  departments,
  leaveTypes,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  departmentFilter,
  setDepartmentFilter,
  leaveTypeFilter,
  setLeaveTypeFilter,
  dateRangeFilter,
  setDateRangeFilter,
  roFilter,
  setRoFilter,
  reportingOfficers = [],
  onApprove,
  isApproving = false,
  onReject,
  onCancel,
  onRefresh,
  onExtendHospitalisation,
  onReconcileHospitalisation,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const isCompact = useIsMobile(APPLICATION_TABLE_BREAKPOINT);
  const activeFiltersCount = (searchTerm ? 1 : 0)
    + (statusFilter !== 'all' ? 1 : 0)
    + (departmentFilter !== 'all' ? 1 : 0)
    + (leaveTypeFilter.length > 0 ? 1 : 0)
    + (dateRangeFilter !== 'all' ? 1 : 0)
    + (roFilter !== 'all' ? 1 : 0);

  const handleSort = (field) => {
    onSortChange(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    );
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) return <ChevronDown className="w-3 h-3 ml-1 text-gray-400 opacity-40" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 text-blue-500" />
      : <ChevronDown className="w-3 h-3 ml-1 text-blue-500" />;
  };

  const totalPages = Math.ceil(totalCount / APPLICATION_PAGE_SIZE);

  const [isExporting, setIsExporting] = useState(false);

  const handleExportApplications = async () => {
    setIsExporting(true);
    try {
      const dirPrefix = sortConfig.direction === 'desc' ? '-' : '';
      const params = {
        ordering: `${dirPrefix}${sortConfig.field}`,
        status_in: 'pending,submitted,verified,approved,auto_approved,rejected,cancelled',
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') {
        params.status = statusFilter;
        delete params.status_in;
      }
      if (departmentFilter !== 'all') params.department = departmentFilter;
      if (leaveTypeFilter.length > 0) params.leave_type_name_in = leaveTypeFilter.join(',');
      if (roFilter !== 'all') params.reporting_officer_id = roFilter;

      if (dateRangeFilter !== 'all') {
        const { after, before } = computeDateRangeBounds(dateRangeFilter);
        if (after) params.start_date_after = after;
        if (before) params.start_date_before = before;
      }

      const response = await staffAPI.exportLeaveApplications(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `leave_applications_${getTodayInSingaporeTimezone()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast.success('Leave applications exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export applications');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Filters and Search */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
          {/* Toolbar: Search (left) + Actions (right) */}
          <div className="flex items-center justify-between">
            {/* Search */}
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search by name, alias, staff ID..."
              theme="admin"
              className="max-w-md"
            />

            {/* Actions */}
            <div className="flex items-center gap-2 ml-4">
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={activeFiltersCount}
                onClick={() => setShowFilters(!showFilters)}
              />
              <button
                onClick={() => handleExportApplications()}
                disabled={isExporting}
                className="icon-button-admin"
                title={isExporting ? 'Exporting…' : 'Export all filtered applications'}
                aria-label="Export"
              >
                <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
              </button>
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDepartmentFilter('all');
                    setLeaveTypeFilter([]);
                    setDateRangeFilter('all');
                    setRoFilter('all');
                  }}
                  className="icon-button-admin"
                  title="Clear all filters"
                  aria-label="Clear all filters"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Active filter chips */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  Search: {searchTerm}
                  <button
                    className="icon-button-admin icon-button-sm ml-2"
                    title="Clear search"
                    onClick={() => setSearchTerm('')}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  Status: {statusFilter}
                  <button
                    className="icon-button-admin icon-button-sm ml-2"
                    title="Clear status"
                    onClick={() => setStatusFilter('all')}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              {departmentFilter !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  Department: {departmentFilter}
                  <button
                    className="icon-button-admin icon-button-sm ml-2"
                    title="Clear department"
                    onClick={() => setDepartmentFilter('all')}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              {leaveTypeFilter.length > 0 && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 max-w-full">
                  <span className="truncate" title={leaveTypeFilter.join(', ')}>
                    Type:{' '}
                    {leaveTypeFilter.length === 1
                      ? leaveTypeFilter[0]
                      : `${leaveTypeFilter.length} types`}
                  </span>
                  <button
                    className="icon-button-admin icon-button-sm ml-2 flex-shrink-0"
                    title="Clear leave type filter"
                    onClick={() => setLeaveTypeFilter([])}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              {dateRangeFilter !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  Date: {formatDateRangeLabel(dateRangeFilter)}
                  <button
                    className="icon-button-admin icon-button-sm ml-2"
                    title="Clear date range"
                    onClick={() => setDateRangeFilter('all')}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
              {roFilter !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-50 text-indigo-800">
                  RO: {reportingOfficers.find(r => r.staff_id === roFilter)?.full_name || roFilter}
                  <button
                    className="icon-button-admin icon-button-sm ml-2"
                    title="Clear reporting officer filter"
                    onClick={() => setRoFilter('all')}
                  >
                    <XCircle className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Collapsible advanced filters panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {/* Status Filter */}
                <div>
                  <PillSelect
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'submitted', label: 'Submitted' },
                      { value: 'hospitalisation_pending_documents', label: 'Certificate pending' },
                      { value: 'reconciliation_required', label: 'Reconciliation required' },
                      { value: 'verified', label: 'Verified' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' },
                      { value: 'cancelled', label: 'Cancelled' },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    theme="admin"
                    icon={Activity}
                    iconColor="text-red-600"
                    className="w-full"
                  />
                </div>

                {/* Department Filter */}
                <div>
                  <PillSelect
                    options={[
                      { value: 'all', label: 'All Departments' },
                      ...departments.map(dept => ({ value: dept.department_name, label: dept.department_name }))
                    ]}
                    value={departmentFilter}
                    onChange={setDepartmentFilter}
                    theme="admin"
                    icon={Building2}
                    iconColor="text-red-600"
                    className="w-full"
                  />
                </div>

                {/* Leave Type Filter — multi-select (empty selection = all types) */}
                <div>
                  <MultiPillSelect
                    options={leaveTypes.map((type) => ({
                      value: type.leave_type_name,
                      label: type.leave_type_name,
                    }))}
                    value={leaveTypeFilter}
                    onChange={setLeaveTypeFilter}
                    allLabel="All Types"
                    theme="admin"
                    icon={Calendar}
                    iconColor="text-red-600"
                    className="w-full"
                    disabled={leaveTypes.length === 0}
                  />
                </div>

                {/* Date Filter — presets + specific month in one popover pill */}
                <div>
                  <DateRangeFilterPill
                    value={dateRangeFilter}
                    onChange={setDateRangeFilter}
                    className="w-full"
                  />
                </div>

                {/* Reporting Officer Filter — SearchableCombobox because list scales with org size */}
                <div>
                  <SearchableCombobox
                    variant="pill"
                    items={reportingOfficers}
                    value={roFilter === 'all' ? '' : roFilter}
                    onChange={(v) => setRoFilter(v || 'all')}
                    placeholder="All Reporting Officers"
                    clearLabel="All Reporting Officers"
                    searchPlaceholder="Search officer name or ID..."
                    displayFn={(ro) => ro.full_name}
                    valueFn={(ro) => ro.staff_id}
                    searchFn={(ro, term) => ro.full_name.toLowerCase().includes(term)}
                    theme="admin"
                    disabled={reportingOfficers.length === 0}
                    icon={User}
                    iconColor="text-red-600"
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-2">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center min-w-0">
            <span className="truncate">Applications</span>
            <CountBadge
              count={totalCount}
              theme="neutral"
              size="md"
              variant="solid"
              max={9999}
              className="ml-2 sm:ml-3 flex-shrink-0"
            />
          </h2>
        </div>

        <div className={`transition-opacity ${isServerLoading ? 'opacity-50' : ''}`}>
          {applications.length === 0 && !isServerLoading ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
              <p className="text-gray-500">No leave applications match your current filters.</p>
            </div>
          ) : isCompact ? (
            <div className="divide-y divide-gray-200">
              {applications.map((application) => (
                <ApplicationRow
                  key={application.id}
                  view="mobile"
                  application={application}
                  onApprove={onApprove}
                  isApproving={isApproving}
                  onReject={() => onReject(application.id)}
                  onCancel={onCancel}
                  onRefresh={onRefresh}
                  onExtendHospitalisation={onExtendHospitalisation}
                  onReconcileHospitalisation={onReconcileHospitalisation}
                />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-fixed min-w-[1440px]">
                <colgroup>
                  <col className="w-[13%]" />
                  <col className="w-[8%]" />
                  <col className="w-[10%]" />
                  <col className="w-[12%]" />
                  <col className="w-[12%]" />
                  <col className="w-[18%]" />
                  <col className="w-[10%]" />
                  <col className="w-[10%]" />
                  <col className="w-[7%]" />
                </colgroup>
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Employee</th>
                    <th className="table-header-cell">Department</th>
                    <th className="table-header-cell">Reporting Officer</th>
                    <th className="table-header-cell">Leave Type</th>
                    <th className="table-header-cell">
                      <button
                        type="button"
                        onClick={() => handleSort('leave_start_date')}
                        className="inline-flex items-center hover:text-gray-900 transition-colors cursor-pointer"
                        title="Sort by Period"
                      >
                        Period <SortIcon field="leave_start_date" />
                      </button>
                    </th>
                    <th className="table-header-cell text-right">
                      <button
                        type="button"
                        onClick={() => handleSort('leave_total_days')}
                        className="inline-flex items-center hover:text-gray-900 transition-colors cursor-pointer ml-auto"
                        title="Sort by Duration"
                      >
                        Duration <SortIcon field="leave_total_days" />
                      </button>
                    </th>
                    <th className="table-header-cell text-center">
                      <button
                        type="button"
                        onClick={() => handleSort('status_priority')}
                        className="inline-flex items-center hover:text-gray-900 transition-colors cursor-pointer"
                        title="Sort by Status"
                      >
                        Status <SortIcon field="status_priority" />
                      </button>
                    </th>
                    <th className="table-header-cell">
                      <button
                        type="button"
                        onClick={() => handleSort('leave_applied_at')}
                        className="inline-flex items-center hover:text-gray-900 transition-colors cursor-pointer"
                        title="Sort by Submitted Date"
                      >
                        Submitted / Updated <SortIcon field="leave_applied_at" />
                      </button>
                    </th>
                    <th className="table-header-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {applications.map((application) => (
                    <ApplicationRow
                      key={application.id}
                      view="desktop"
                      application={application}
                      onApprove={onApprove}
                      isApproving={isApproving}
                      onReject={() => onReject(application.id)}
                      onCancel={onCancel}
                      onRefresh={onRefresh}
                      onExtendHospitalisation={onExtendHospitalisation}
                      onReconcileHospitalisation={onReconcileHospitalisation}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          currentPage={serverPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          totalCount={totalCount}
          pageSize={APPLICATION_PAGE_SIZE}
          theme="admin"
          noun="applications"
        />
      </div>
    </div>
  );
};

// DocumentViewer component has been extracted into MCDocumentVerificationModal

// OIL Attendance Display Component - Shows employee's clock-in/out for OIL approval
const OILAttendanceDisplay = ({ employeeId, workDate }) => {
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState(null);

  useEffect(() => {
    const fetchAttendance = async () => {
      if (!employeeId || !workDate) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        // Call API to get employee's clock events for the work date
        const response = await staffAPI.getClockEvents({ 
          staff_id: employeeId,
          date: workDate 
        });
        const events = response?.data?.results || response?.data || [];
        const clockIn = events.find(e => e.clock_event_type === 'clock_in');
        const clockOut = events.find(e => e.clock_event_type === 'clock_out');
        setAttendanceData({ clockIn, clockOut, events });
      } catch (error) {
        console.error('Failed to fetch attendance:', error);
        setAttendanceData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAttendance();
  }, [employeeId, workDate]);

  // Helper to format time
  const formatTime = (timeStr) => {
    if (!timeStr) return '—';
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return timeStr;
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading attendance data...</p>;
  }
  
  if (!attendanceData?.clockIn && !attendanceData?.clockOut) {
    return (
      <div className="flex items-center gap-2 text-amber-600">
        <AlertTriangle className="w-4 h-4" />
        <span className="text-sm">No attendance records found for this date</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white p-3 rounded-lg border">
        <p className="text-xs text-gray-500 mb-1">Clock In</p>
        <p className="text-sm font-semibold text-green-700">
          {attendanceData.clockIn ? formatTime(attendanceData.clockIn.clock_event_timestamp) : '—'}
        </p>
      </div>
      <div className="bg-white p-3 rounded-lg border">
        <p className="text-xs text-gray-500 mb-1">Clock Out</p>
        <p className="text-sm font-semibold text-red-700">
          {attendanceData.clockOut ? formatTime(attendanceData.clockOut.clock_event_timestamp) : '—'}
        </p>
      </div>
    </div>
  );
};

// Application Row Component
const ApplicationRow = ({
  application, onApprove, isApproving = false, onReject, onCancel, onRefresh,
  onExtendHospitalisation, onReconcileHospitalisation, view = 'desktop',
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [mcVerificationOpen, setMcVerificationOpen] = useState(false);
  const [nsVerificationModal, setNsVerificationModal] = useState({ open: false, applicationId: null, employeeName: null, employeeStaffCode: null });

  const leaveTypeName = getAppliedLeaveTypeName(application);
  const displayLeaveTypeName = getDisplayLeaveTypeName(application);
  const oilCreditedDays = getOilCreditedDays(application);
  const isOutpatientSickLeave = leaveTypeName.toLowerCase().includes('outpatient') && leaveTypeName.toLowerCase().includes('sick');
  const isHospitalisationLeave = leaveTypeName.toLowerCase().includes('hospitalisation');
  const isSickLeaveType = isOutpatientSickLeave || isHospitalisationLeave;
  // Check multiple possible field names for MC upload status
  const hasUploadedMc = !!(application.has_medical_certificate ||
                          application.has_mc_uploaded ||
                          application.mc_uploaded ||
                          (application.leave_medical_cert_path && application.leave_medical_cert_path.trim()));
  const shouldShowMcView = isSickLeaveType && (application.mc_required || hasUploadedMc);
  const isNSLeave = isNSLeaveName(leaveTypeName);

  // Compassionate Leave: advisory secondary line under Status (same pattern as L1/L2).
  const compassionateDocStatusLabel = (() => {
    const ds = application.compassionate_document?.doc_status;
    if (!ds || ds === 'verified') return null;
    if (ds === 'rejected') return 'Cert: Rejected';
    if (ds === 'pending_verification') return 'Cert: Pending';
    return 'Cert: Awaiting';
  })();

  // Trade chip only when it adds info beyond department (e.g. Teams → I&C).
  const showTradeBadge =
    Boolean(application.trade_code) &&
    !isTradeRedundantWithDepartment(application.department_name, application.trade_code);

  const hasDeclaredOverseasTravel = application.overseas_travel_declared === true;
  const travelStart = application.overseas_departure_date;
  const travelEnd = application.overseas_return_date;
  const travelDays = hasDeclaredOverseasTravel
    ? inclusiveCalendarDays(travelStart, travelEnd)
    : 0;
  const travelStartLabel = travelStart && travelEnd &&
    getYearInSingapore(travelStart) === getYearInSingapore(travelEnd)
    ? formatDateShort(travelStart)
    : formatDate(travelStart);
  const travelTitle = travelDays >= MIN_OVERSEAS_CONSECUTIVE_DAYS
    ? `Declared overseas travel (${formatLeaveDays(travelDays)} calendar ${pluralizeDays(travelDays)}). Review levy-waiver eligibility after return.`
    : travelDays > 0
      ? `Declared overseas travel (${formatLeaveDays(travelDays)} calendar ${pluralizeDays(travelDays)}; under the levy-waiver threshold).`
      : 'Declared overseas travel. Open the application for details.';
  const overseasTravelMeta = hasDeclaredOverseasTravel ? (
    <div
      className="mt-1 flex items-center gap-1 whitespace-nowrap text-[11px] leading-tight text-sky-700"
      title={travelTitle}
      aria-label={
        travelStart && travelEnd
          ? `Overseas travel ${travelStartLabel} to ${formatDate(travelEnd)}`
          : 'Overseas travel declared'
      }
    >
      <Plane className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
      {travelStart && travelEnd ? (
        <span className="tabular-nums">
          {travelStartLabel}
          <span className="text-gray-400 mx-0.5" aria-hidden="true">→</span>
          {formatDate(travelEnd)}
        </span>
      ) : null}
    </div>
  ) : null;

  // Not a pill: coloured pills in this column mean leave types (amber = OSL).
  const extensionMeta = application.is_extension ? (
    <p
      className="mt-0.5 flex items-center gap-0.5 whitespace-nowrap text-[10px] leading-tight text-gray-500"
      title={`Extension of an earlier ${leaveTypeName || 'leave'} trip`}
    >
      <CornerDownRight className="w-3 h-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
      Extension
    </p>
  ) : null;

  // Note: deep-links (?application=<id>) are handled at page level in
  // LeaveManagement, which fetches the application directly so the modal
  // opens even when the row is not on the current table page.

  const openNSVerificationModal = () => {
    setNsVerificationModal({
      open: true,
      applicationId: application.id,
      employeeName: application.employee_name || null,
      employeeStaffCode: application.staff_code || null,
    });
  };

  const handleShowDetails = () => {
    if (shouldShowMcView) {
      setMcVerificationOpen(true);
    } else if (isNSLeave) {
      openNSVerificationModal();
    } else {
      setShowDetails(true);
    }
  };

  const statusBadge = (
    <StatusBadge
      status={application.hospitalisation_ui_status || application.leave_status}
      {...(isSickLeaveType && application.leave_status === 'approved' && !application.hospitalisation_ui_status ? { label: 'Verified' } : {})}
    />
  );

  const cancellationImpact = application.leave_status === 'cancelled'
    ? (application.cancellation_impact || null)
    : null;
  const cancellationPills = cancellationImpact?.pills || [];

  const statusSecondaryLines = (
    <>
      {application.leave_status === 'submitted' && application.approval_hierarchy && application.approval_hierarchy.length > 1 && (() => {
        const total = application.approval_hierarchy.length;
        const current = application.current_approval_level || 1;
        const currentEntry = application.approval_hierarchy.find(h => h.level === current);
        const roleName = currentEntry ? currentEntry.role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
        return (
          <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">
            L{current}/{total} · {roleName || 'Pending'}
          </p>
        );
      })()}
      {compassionateDocStatusLabel && (
        <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">
          {compassionateDocStatusLabel}
        </p>
      )}
      {application.leave_status === 'cancelled' && application.cancelled_by_name && (
        <p className="text-[10px] text-gray-500 mt-0.5 leading-tight truncate" title={application.cancelled_by_name}>
          by {application.cancelled_by_name}
        </p>
      )}
    </>
  );

  const cancellationDetailContent = application.leave_status === 'cancelled' ? (
    <div className="mt-1 space-y-1 min-w-0">
      {cancellationPills.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {cancellationPills.map((pill) => (
            <InfoBadge
              key={pill}
              color="red"
              label={pill}
              size="xs"
            />
          ))}
        </div>
      )}
      {application.cancellation_reason ? (
        <p
          className="text-[10px] text-gray-500 leading-tight truncate"
          title={application.cancellation_reason}
        >
          {application.cancellation_reason}
        </p>
      ) : null}
    </div>
  ) : null;

  const totalDaysValue = getDisplayTotalDays(application, application.leave_days);
  const hasBreakdown = oilCreditedDays == null && hasCrossTypeConsumption(application, displayLeaveTypeName);
  const formattedTotalDays = formatLeaveDays(totalDaysValue);
  const formulaTitle = hasBreakdown
    ? getConsumptionFormulaTitle(application.leave_consumption_proposal)
    : '';
  const durationTitle = hasBreakdown && formulaTitle
    ? `${formattedTotalDays} ${pluralizeDays(totalDaysValue)} · ${formulaTitle}`
    : undefined;

  const durationContent = (
    <DurationWithConsumption
      showBreakdown={hasBreakdown}
      proposal={application.leave_consumption_proposal}
      justify={view === 'desktop' ? 'end' : 'start'}
      compact
      nowrap={view === 'desktop'}
      title={durationTitle}
    >
      {oilCreditedDays != null ? (
        <span className={getOilCreditDaysClassName(application.leave_status)}>
          {formatOilCreditDaysLabel(oilCreditedDays, application.leave_status)}
        </span>
      ) : (
        <>
          <span className="font-medium tabular-nums text-sm text-gray-900">
            {formattedTotalDays || '—'}
          </span>
          {formattedTotalDays ? (
            <span className="text-xs text-gray-500 ml-0.5">
              {hasBreakdown ? 'd' : pluralizeDays(totalDaysValue)}
            </span>
          ) : null}
        </>
      )}
    </DurationWithConsumption>
  );

  const timestampsContent = (
    <div className="min-w-0">
      <div className="flex items-center text-sm text-gray-900">
        <FileText className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          {application.leave_applied_at ? (
            <div className="text-xs text-gray-900 truncate tabular-nums" title={formatDateTime(application.leave_applied_at)}>
              {formatDateTime(application.leave_applied_at)}
            </div>
          ) : '—'}
        </div>
      </div>
      {application.leave_status === 'cancelled' && application.cancelled_at ? (
        <div className="mt-1 min-w-0">
          <div className="flex items-center text-sm text-gray-900">
            <XCircle className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-gray-900 truncate tabular-nums" title={formatDateTime(application.cancelled_at)}>
                {formatDateTime(application.cancelled_at)}
              </div>
            </div>
          </div>
          {cancellationDetailContent}
        </div>
      ) : application.leave_approved_at && application.leave_status !== 'cancelled' ? (
        <div className="flex items-center text-sm text-gray-900 mt-1">
          <CheckCircle className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-gray-900 truncate tabular-nums" title={formatDateTime(application.leave_approved_at)}>
              {formatDateTime(application.leave_approved_at)}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  const actionButtons = (
    <div className="flex justify-end items-center gap-1">
      <button
        type="button"
        onClick={handleShowDetails}
        className="icon-button-admin"
        title="View Details"
        aria-label="View Details"
      >
        <Eye className="w-4 h-4" />
      </button>
    </div>
  );

  const modals = (
    <>
      {showDetails && (
        <LeaveApplicationDetailModal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          applicationId={application.id}
          onApprove={onApprove}
          isApproving={isApproving}
          onReject={onReject ? () => { onReject(application.id); setShowDetails(false); } : undefined}
          onCancel={onCancel ? (id) => { onCancel(id); setShowDetails(false); } : undefined}
          onRefresh={onRefresh}
          extraFooterActions={
            application.hospitalisation_ui_status ? (
              <>
                {application.hospitalisation_ui_status === 'hospitalisation_pending_documents' && (
                  <button
                    type="button"
                    className="btn btn-admin-outline"
                    onClick={() => onExtendHospitalisation?.(application)}
                  >
                    Extend coverage
                  </button>
                )}
                {application.hospitalisation_ui_status === 'reconciliation_required' && (
                  <button
                    type="button"
                    className="btn btn-admin-primary"
                    onClick={() => onReconcileHospitalisation?.(application)}
                  >
                    Reconcile dates
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-admin-primary"
                  onClick={() => { setShowDetails(false); setMcVerificationOpen(true); }}
                >
                  Upload / verify MC
                </button>
                <button type="button" onClick={() => setShowDetails(false)} className="btn btn-secondary">
                  Close
                </button>
              </>
            ) : undefined
          }
        />
      )}

      <MCDocumentVerificationModal
        key={application.id}
        isOpen={mcVerificationOpen}
        onClose={() => setMcVerificationOpen(false)}
        applicationId={application.id}
        application={application}
        onVerificationComplete={() => {
          setMcVerificationOpen(false);
          if (onRefresh) onRefresh();
        }}
      />

      <NSDocumentVerificationModal
        key={nsVerificationModal.applicationId}
        isOpen={nsVerificationModal.open}
        onClose={() => setNsVerificationModal({ open: false, applicationId: null, employeeName: null, employeeStaffCode: null })}
        applicationId={nsVerificationModal.applicationId}
        employeeName={nsVerificationModal.employeeName}
        employeeStaffCode={nsVerificationModal.employeeStaffCode}
        onVerificationComplete={() => {
          setNsVerificationModal({ open: false, applicationId: null, employeeName: null, employeeStaffCode: null });
          if (onRefresh) onRefresh();
        }}
      />
    </>
  );

  if (view === 'mobile') {
    return (
      <>
        <div className="px-6 py-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{application.employee_name}</p>
              <p className="text-xs text-gray-500">
                {application.department_name ? (
                  <InfoBadge
                    color={getDepartmentBadgeColor(application.department_name)}
                    label={getDepartmentShortName(application.department_name)}
                    title={application.department_name}
                    size="xs"
                  />
                ) : ''}
                {showTradeBadge && (
                  <>
                    {' '}
                    <TradeBadge code={application.trade_code} size="xs" />
                  </>
                )}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              {statusBadge}
              {statusSecondaryLines}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600 gap-2">
            <div className="min-w-0">
              <LeaveTypeBadge leaveTypeName={displayLeaveTypeName} size="xs" compact />
              {extensionMeta}
            </div>
            <div className="text-right flex-shrink-0">
              {durationContent}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <LeavePeriodDisplay application={application} showIcon={false} size="xs" className="text-gray-500" />
              {overseasTravelMeta}
            </div>
            <button
              type="button"
              onClick={handleShowDetails}
              className="icon-button-admin"
              title="View Details"
              aria-label="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
          {application.leave_status === 'cancelled' && cancellationDetailContent}
        </div>
        {modals}
      </>
    );
  }

  return (
    <>
      <tr className="table-row">
        <td className="table-cell">
          <div className="min-w-0 max-w-full">
            <p className="text-sm font-medium text-gray-900 truncate" title={application.employee_name}>
              {application.employee_name}
            </p>
            <p className="text-xs text-gray-500 truncate" title={application.staff_code || ''}>
              {application.staff_code || ''}
            </p>
          </div>
        </td>
        <td className="table-cell whitespace-normal">
          <div className="min-w-0 max-w-full overflow-hidden">
            {application.department_name ? (
              <InfoBadge
                color={getDepartmentBadgeColor(application.department_name)}
                label={getDepartmentShortName(application.department_name)}
                title={application.department_name}
              />
            ) : null}
            {showTradeBadge && (
              <div className="mt-0.5">
                <TradeBadge code={application.trade_code} size="xs" />
              </div>
            )}
          </div>
        </td>
        <td className="table-cell">
          <div className="min-w-0 max-w-full">
            {application.reporting_officer_name ? (
              <>
                <p className="text-sm text-gray-900 truncate" title={application.reporting_officer_name}>
                  {application.reporting_officer_name}
                </p>
                {application.reporting_officer_staff_id && (
                  <p className="text-xs text-gray-500 truncate">{application.reporting_officer_staff_id}</p>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400">—</span>
            )}
          </div>
        </td>
        <td className="table-cell">
          <div className="min-w-0 max-w-full overflow-hidden">
            <LeaveTypeBadge leaveTypeName={displayLeaveTypeName} compact />
            {extensionMeta}
          </div>
        </td>
        <td className="table-cell">
          <div className="min-w-0 max-w-full">
            <LeavePeriodDisplay application={application} showIcon={false} size="sm" noWrap />
            {overseasTravelMeta}
          </div>
        </td>
        <td className="table-cell text-right">
          {durationContent}
        </td>
        <td className="table-cell text-center">
          <div className="inline-flex flex-col items-center max-w-full">
            {statusBadge}
            {statusSecondaryLines}
          </div>
        </td>
        <td className="table-cell whitespace-normal">
          {timestampsContent}
        </td>
        <td className="table-cell text-right">
          {actionButtons}
        </td>
      </tr>
      {modals}
    </>
  );
};

const LEAVE_TYPE_GROUPS = {
  'Childcare Leave': ['Childcare Leave', 'Childcare Leave (Non-Citizen)', 'Extended Childcare Leave'],
  'Maternity Leave': ['Maternity Leave (Citizen Child)', 'Maternity Leave (Non-Citizen Child)', 'Maternity Leave (Unpaid)'],
  'Compassionate Leave': ['Compassionate Leave (Immediate Family)', 'Compassionate Leave (Grandparent)'],
};

const LEAVE_TYPE_GROUP_REVERSE = Object.entries(LEAVE_TYPE_GROUPS).reduce((acc, [group, variants]) => {
  variants.forEach(v => { acc[v] = group; });
  return acc;
}, {});

const getLeaveTypeName = (app) => {
  return app.leave_type_name ||
         app.leave_type?.leave_type_name ||
         app.leave_type_id?.leave_type_name ||
         'Unknown';
};

// getLeaveTypeBadgeClass is now imported from utils/leaveTypeBadgeColors.js

const CALENDAR_LEGEND_ITEMS = LEAVE_LEGEND_ITEMS;


const CalendarTab = ({ departments, publicHolidays = [], leaveTypes = [] }) => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState([]);
  const [selectedDateApplications, setSelectedDateApplications] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);

  const [applications, setApplications] = useState([]);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);

  const leaveTypeOptions = useMemo(() => {
    const seen = new Set();
    const items = [];
    leaveTypes.forEach(lt => {
      const display = LEAVE_TYPE_GROUP_REVERSE[lt.leave_type_name] || lt.leave_type_name;
      if (!seen.has(display)) {
        seen.add(display);
        items.push({ value: display, label: display });
      }
    });
    return items;
  }, [leaveTypes]);

  const departmentOptions = useMemo(() =>
    departments.map(d => ({ value: d.department_name, label: d.department_name })),
    [departments]
  );

  const calendarMonthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;

  useEffect(() => {
    const abortController = new AbortController();

    const fetchCalendarData = async () => {
      setIsLoadingCalendar(true);
      try {
        const [year, month] = calendarMonthKey.split('-').map(Number);
        const windowStart = new Date(year, month - 1, 1);
        const windowEnd = new Date(year, month + 2, 0);

        const pad = (n) => String(n).padStart(2, '0');
        const startStr = `${windowStart.getFullYear()}-${pad(windowStart.getMonth() + 1)}-${pad(windowStart.getDate())}`;
        const endStr = `${windowEnd.getFullYear()}-${pad(windowEnd.getMonth() + 1)}-${pad(windowEnd.getDate())}`;

        const result = await staffAPI.getLeaveApplications({
          end_date_after: startStr,
          start_date_before: endStr,
          status_in: 'approved,auto_approved',
          page_size: 5000,
          ordering: '-leave_start_date',
        }, { signal: abortController.signal });

        if (!abortController.signal.aborted) {
          const data = result.data;
          const rawApps = Array.isArray(data) ? data : data?.results || [];
          setApplications(rawApps);
        }
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('Failed to load calendar data:', err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingCalendar(false);
        }
      }
    };

    fetchCalendarData();
    return () => abortController.abort();
  }, [calendarMonthKey]);

  const matchesLeaveTypeFilter = useCallback((appLeaveTypeName) => {
    if (selectedLeaveTypes.length === 0) return true;
    const groupKey = LEAVE_TYPE_GROUP_REVERSE[appLeaveTypeName];
    if (groupKey && selectedLeaveTypes.includes(groupKey)) return true;
    return selectedLeaveTypes.includes(appLeaveTypeName);
  }, [selectedLeaveTypes]);

  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(app.department_name)) return false;

      const leaveTypeName = getLeaveTypeName(app);

      if (!matchesLeaveTypeFilter(leaveTypeName)) return false;

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const name = (app.employee_name || '').toLowerCase();
        const staffCode = (app.staff_code || '').toLowerCase();
        if (!name.includes(term) && !staffCode.includes(term)) return false;
      }

      return true;
    });
  }, [applications, selectedDepartments, selectedLeaveTypes, searchTerm, matchesLeaveTypeFilter]);

  const getApplicationsForDate = useCallback((date) => {
    if (!date) return [];

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return filteredApplications.filter(app => {
      const startDateStr = app.leave_start_date ? app.leave_start_date.split('T')[0] : '';
      const endDateStr = app.leave_end_date ? app.leave_end_date.split('T')[0] : '';
      return dateStr >= startDateStr && dateStr <= endDateStr;
    });
  }, [filteredApplications]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const isPublicHoliday = (date) => {
    if (!date) return false;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return publicHolidays.some(h => h.date === dateStr);
  };

  const getPublicHolidayName = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    const hit = publicHolidays.find(h => h.date === dateStr);
    return hit?.name || 'Public Holiday';
  };

  const getDateColor = (date) => {
    if (isPublicHoliday(date)) return 'bg-red-200 hover:bg-red-300 border-red-400';
    return 'bg-white hover:bg-gray-50';
  };

  const handleDateClick = (date) => {
    const apps = getApplicationsForDate(date);
    setSelectedDateApplications(apps);
    setSelectedDate(date);
    setShowDateModal(true);
  };

  const renderStatusBadge = useCallback((app) => {
    const leaveTypeName = app.leave_type?.leave_type_name || app.leave_type_id?.leave_type_name || app.leave_type_name || app.leave_type || 'N/A';
    const isSickLeave = (() => {
      const lt = (leaveTypeName || '').toLowerCase();
      return (lt.includes('outpatient') && lt.includes('sick')) || lt.includes('hospitalisation');
    })();
    return <StatusBadge status={app.hospitalisation_ui_status || app.leave_status} {...(isSickLeave && app.leave_status === 'approved' && !app.hospitalisation_ui_status ? { label: 'Verified' } : {})} />;
  }, []);

  const renderLeaveTypeInline = useCallback((app) => {
    const leaveTypeName = app.leave_type?.leave_type_name || app.leave_type_id?.leave_type_name || app.leave_type_name || app.leave_type || 'N/A';
    return <LeaveTypeBadge leaveTypeName={leaveTypeName} size="xs" />;
  }, []);

  const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const handleMonthNavigate = (newYm) => {
    const [ny, nm] = newYm.split('-').map(Number);
    setCurrentDate(new Date(ny, nm - 1, 1));
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentDate);

  const hasActiveFilters = selectedDepartments.length > 0 || searchTerm || selectedLeaveTypes.length > 0;

  const clearAllFilters = () => {
    setSelectedDepartments([]);
    setSearchTerm('');
    setSelectedLeaveTypes([]);
  };

  return (
    <div className="space-y-6">
      {/* Calendar Controls */}
      <div className="bg-white rounded-3xl border-2 border-gray-200 px-6 py-4 space-y-3">
        {/* Row 1: Month navigation + Today */}
        <div className="flex justify-between items-center">
          <MonthNavigator
            month={currentMonthKey}
            onChange={handleMonthNavigate}
            theme="admin"
          />

          <button
            onClick={() => setCurrentDate(new Date())}
            className="btn btn-admin-primary"
          >
            Today
          </button>
        </div>

        {/* Row 2: Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Employee search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, alias, or staff ID..."
              className="input-admin pl-9 pr-8 py-2 w-64 text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Department multi-select filter */}
          <MultiPillSelect
            options={departmentOptions}
            value={selectedDepartments}
            onChange={setSelectedDepartments}
            allLabel="All Departments"
            icon={Building2}
            iconColor="text-red-600"
            theme="admin"
          />

          {/* Leave type multi-select */}
          <MultiPillSelect
            options={leaveTypeOptions}
            value={selectedLeaveTypes}
            onChange={setSelectedLeaveTypes}
            allLabel="All Leave Types"
            icon={Calendar}
            iconColor="text-red-600"
            theme="admin"
            renderItem={(option) => (
              <>
                <span className={`w-3 h-3 rounded-sm flex-shrink-0 border ${getLeaveTypeBadgeClass(option.label).replace(/text-\S+/, '').trim()}`} />
                <span className="text-sm text-gray-700">{option.label}</span>
              </>
            )}
          />

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-3xl border-2 border-gray-200 overflow-hidden relative">
        {isLoadingCalendar && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        )}
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50">
          {dayNames.map(day => (
            <div key={day} className="p-4 text-center text-sm font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((date, index) => {
            const dayApps = date ? getApplicationsForDate(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();

            return (
              <div
                key={index}
                className={`min-h-[120px] p-2 border-r border-b border-gray-200 last:border-r-0 ${
                  date ? `cursor-pointer ${getDateColor(date)}` : 'bg-gray-25'
                } ${isToday ? 'ring-2 ring-red-500 ring-inset' : ''}`}
                onClick={() => date && handleDateClick(date)}
              >
                {date && (
                  <>
                    <div className={`flex flex-col ${isToday ? 'text-red-600' : 'text-gray-900'}`}>
                      <div className="text-sm font-medium mb-1">{date.getDate()}</div>
                      {isPublicHoliday(date) && (
                        <div
                          className="badge badge-error text-xs leading-tight py-1 px-2 whitespace-normal break-words"
                          title={getPublicHolidayName(date)}
                          aria-label={getPublicHolidayName(date)}
                          style={{fontSize: '10px', lineHeight: '1.2'}}
                        >
                          {getPublicHolidayName(date)}
                        </div>
                       )}
                    </div>
                    <div className="space-y-1">
                      {dayApps.slice(0, 3).map((app, appIndex) => {
                        const leaveTypeName = getLeaveTypeName(app);
                        const badgeClass = getLeaveTypeBadgeClass(leaveTypeName);

                        return (
                          <div
                            key={appIndex}
                            className={`text-xs p-1 rounded truncate ${badgeClass}`}
                            title={`${app.employee_name} - ${leaveTypeName}`}
                          >
                            {app.employee_name}
                          </div>
                        );
                      })}
                      {dayApps.length > 3 && (
                        <div className="text-xs text-gray-500 font-medium">
                          +{dayApps.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-5 gap-y-2 px-6 py-4 border-t border-gray-200">
          {CALENDAR_LEGEND_ITEMS.map(item => (
            <div key={item.label} className="flex items-center space-x-1.5">
              <div className={`w-3 h-3 ${item.color} rounded`}></div>
              <span className="text-xs text-gray-500">
                {item.abbr && <span className="font-medium text-gray-600">{item.abbr}</span>}
                {item.abbr ? ` ${item.label}` : item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Date Details Modal */}
      <GroupedDateLeaveModal
        show={showDateModal}
        onClose={() => setShowDateModal(false)}
        date={selectedDate}
        applications={selectedDateApplications}
        userDepartment={user?.department?.department_name}
        isAdmin
        renderBadge={renderStatusBadge}
        renderLeaveTypeInline={renderLeaveTypeInline}
      />
    </div>
  );
};

export default LeaveManagement;